import type Stripe from "stripe";
import { Frequency } from "../utils/date.js";
import { type ItemPriceTier, type PlanSlug } from "./Account.js";

/**
 * Value of the `billing_item_type` metadata on a Price, used to tell items
 * on a subscription apart: the base Price is tagged "base", each add-on
 * Price gets its own type (e.g. "seat").
 */
export enum SubscriptionItemType {
  Base = "base",
  Seat = "seat",
  Project = "project",
}

/**
 * The planSlug used when no plan is explicitly specified, e.g. when
 * provisioning a brand new customer.
 */
export const DEFAULT_PLAN_SLUG: PlanSlug = "starter";

export type AddonConfig = {
  itemType: SubscriptionItemType;
  priceId: string;
  tiers: ItemPriceTier[] | null;
  /**
   * Quantity included in the base price before the first paid unit, derived
   * from the addon's own tiers (0 if its first tier isn't free).
   */
  included: number;
  /**
   * Default quantity for this add-on when subscribing, taken from the
   * Payment Link's line item quantity.
   */
  quantity: number;
  /**
   * The most units a customer can buy, from the Payment Link's adjustable
   * quantity. null means no ceiling.
   */
  maxQuantity: number | null;
};

export type PlanConfig = {
  planSlug: string;
  interval: Frequency;
  name: string;
  description: string | null;
  marketingFeatures: string[];
  basePriceId: string;
  /**
   * Default quantity for the base price, taken from the Payment Link's
   * line item quantity.
   */
  baseQuantity: number;
  addons: AddonConfig[];
  baseUnitAmount: number | null;
  paymentLink: string;
  trialDays: number | null;
};

export enum StripeCacheKeys {
  PromoCodes = "promo-codes",
  Price = "price",
  Plans = "plans",
}

export type SubscriptionStatus = Stripe.Subscription["status"];

export const validStatuses: Stripe.Subscription["status"][] = [
  "active",
  "trialing",
  "paused",
  "past_due",
];

/**
 * Statuses that unlock the account. Everything else suspends it: incomplete
 * and incomplete_expired were never paid, past_due and unpaid owe an invoice,
 * canceled and paused have nothing running.
 */
export const accountActiveStatuses: SubscriptionStatus[] = [
  "active",
  "trialing",
];

/**
 * A subscription in one of these still owes money, so the free plan is not a
 * way out of it.
 */
export const freePlanBlockingStatuses: SubscriptionStatus[] = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
];

/** Paused is excluded: a paused account is inactive and resubscribing is its way back. */
export const liveSubscriptionStatuses: Stripe.Subscription["status"][] = [
  "active",
  "trialing",
  "past_due",
];

export type PlanChangeDirection = "current" | "upgrade" | "downgrade";

type PlanPrice = {
  planSlug: string;
  interval: Frequency;
  baseUnitAmount: number | null;
};

function monthlyAmount({ interval, baseUnitAmount }: PlanPrice) {
  return (baseUnitAmount ?? 0) / (interval === Frequency.Year ? 12 : 1);
}

/** On the same plan, going yearly is an upgrade even though it's cheaper per month. */
export function getPlanChangeDirection(
  current: PlanPrice,
  target: PlanPrice,
): PlanChangeDirection {
  if (current.planSlug === target.planSlug) {
    if (current.interval === target.interval) return "current";
    return target.interval === Frequency.Year ? "upgrade" : "downgrade";
  }

  return monthlyAmount(target) > monthlyAmount(current)
    ? "upgrade"
    : "downgrade";
}
